import { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useTranslation } from "react-i18next";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import { ConditionBadge } from "@/components/listings/ConditionBadge";
import { BoundingBoxOverlay } from "@/components/listings/BoundingBoxOverlay";
import { ListingCard } from "@/components/listings/ListingCard";
import { ListingPriceComparison } from "@/components/listings/ListingPriceComparison";
import { ZoomedListingImage } from "@/components/listings/ZoomedListingImage";
import { SiblingsPhotoMap } from "@/components/listings/SiblingsPhotoMap";
import { SaveListingButton } from "@/components/listings/SaveListingButton";
import { SellerTrustBlock } from "@/components/listings/SellerTrustBlock";
import { ListingFreshness } from "@/components/listings/ListingFreshness";
import { RatingBadge } from "@/components/ui/RatingBadge";
import type { Listing, BoundingBox, Game } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ListingPlaceholder } from "@/components/ui/ListingPlaceholder";

const LISTING_QUERY = gql`
  query Listing($id: ID!) {
    listing(id: $id) {
      id
      price
      condition
      conditionLabel
      conditionNotes
      language
      languageLabel
      shippingAvailable
      shippingCost
      status
      location
      deliveryAvailable
      pickupAvailable
      pickupCity
      pickupRadius
      totalPrice
      imageUrls
      displayTitle
      displayImageUrl
      createdAt
      listingGroupId
      boundingBox {
        yMin
        xMin
        yMax
        xMax
      }
      siblingListings {
        id
        status
        gameTitle
        price
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
      }
      siblingListingDetails {
        id
        price
        condition
        conditionLabel
        status
        imageUrls
        displayTitle
        displayImageUrl
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        siblingListings {
          id
          status
          gameTitle
          price
          boundingBox {
            yMin
            xMin
            yMax
            xMax
          }
        }
        game {
          id
          title
          imageUrl
          thumbnailUrl
        }
        seller {
          id
          displayName
        }
      }
      sellerOtherListings {
        id
        price
        condition
        conditionLabel
        imageUrls
        displayTitle
        displayImageUrl
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        siblingListings {
          id
          status
          gameTitle
          price
          boundingBox {
            yMin
            xMin
            yMax
            xMax
          }
        }
        game {
          id
          title
          imageUrl
          thumbnailUrl
        }
        seller {
          id
          displayName
        }
      }
      game {
        id
        title
        description
        imageUrl
        thumbnailUrl
        yearPublished
        playerRange
        playtimeRange
        complexityLabel
        bggId
        bggRating
        displayRating
        ratingSource
        shopListings {
          id
          shopName
          listingUrl
          price
          priceUpdatedAt
          updatedAt
        }
      }
      seller {
        id
        displayName
        avatarUrl
        rating
        ratingsCount
        createdAt
        activeListingsCount
        soldListingsCount
      }
      savedByMe
    }
  }
`;

const UPDATE_LISTING_BBOX_MUTATION = gql`
  mutation UpdateListing($id: ID!, $clearBoundingBox: Boolean, $bboxYMin: Int, $bboxXMin: Int, $bboxYMax: Int, $bboxXMax: Int) {
    updateListing(id: $id, clearBoundingBox: $clearBoundingBox, bboxYMin: $bboxYMin, bboxXMin: $bboxXMin, bboxYMax: $bboxYMax, bboxXMax: $bboxXMax) {
      listing {
        id
      }
      errors
    }
  }
`;

const UPDATE_LISTING_STATUS_MUTATION = gql`
  mutation UpdateListingStatus($id: ID!, $status: String!) {
    updateListing(id: $id, status: $status) {
      listing {
        id
        status
      }
      errors
    }
  }
`;

const CANCEL_LISTING_MUTATION = gql`
  mutation CancelListing($id: ID!) {
    cancelListing(id: $id) {
      success
      errors
    }
  }
`;

const SEARCH_GAMES_QUERY = gql`
  query SearchGames($query: String!) {
    searchGames(query: $query) {
      id
      title
      yearPublished
      thumbnailUrl
    }
  }
`;

const CREATE_LISTING_MUTATION_SIBLING = gql`
  mutation CreateListing(
    $gameId: ID
    $title: String
    $price: Float!
    $condition: String!
    $listingGroupId: String
    $bboxYMin: Int
    $bboxXMin: Int
    $bboxYMax: Int
    $bboxXMax: Int
    $deliveryAvailable: Boolean
    $pickupAvailable: Boolean
  ) {
    createListing(
      gameId: $gameId
      title: $title
      price: $price
      condition: $condition
      listingGroupId: $listingGroupId
      bboxYMin: $bboxYMin
      bboxXMin: $bboxXMin
      bboxYMax: $bboxYMax
      bboxXMax: $bboxXMax
      deliveryAvailable: $deliveryAvailable
      pickupAvailable: $pickupAvailable
    ) {
      listing {
        id
      }
      errors
    }
  }
`;

const CONDITION_KEYS = ["new", "like_new", "good", "acceptable"] as const;

type GameInfo = Pick<Game, "id" | "title" | "yearPublished" | "thumbnailUrl">;

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["listing", id],
    queryFn: () =>
      graphqlClient.request<{ listing: Listing | null }>(LISTING_QUERY, {
        id,
      }),
    enabled: !!id,
  });


  const listing = data?.listing;

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [newBoxDrawn, setNewBoxDrawn] = useState<BoundingBox | null>(null);
  const [gameSearch, setGameSearch] = useState("");
  const [selectedGame, setSelectedGame] = useState<GameInfo | null>(null);
  const [newPrice, setNewPrice] = useState("");
  const [newCondition, setNewCondition] = useState("good");

  const { data: searchResults } = useQuery({
    queryKey: ["searchGames", gameSearch],
    queryFn: () =>
      graphqlClient.request<{ searchGames: GameInfo[] }>(SEARCH_GAMES_QUERY, { query: gameSearch }),
    enabled: gameSearch.length >= 2,
  });

  const clearBboxMutation = useMutation({
    mutationFn: (listingId: string) =>
      graphqlClient.request(UPDATE_LISTING_BBOX_MUTATION, { id: listingId, clearBoundingBox: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listing", id] });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (vars: { id: string; status: string }) =>
      graphqlClient.request<{ updateListing: { listing: { id: string; status: string } | null; errors: string[] } }>(
        UPDATE_LISTING_STATUS_MUTATION,
        vars
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listing", id] });
      queryClient.invalidateQueries({ queryKey: ["myListings"] });
    },
  });

  const cancelSiblingMutation = useMutation({
    mutationFn: (siblingId: string) =>
      graphqlClient.request(CANCEL_LISTING_MUTATION, { id: siblingId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listing", id] });
    },
  });

  const createSiblingMutation = useMutation({
    mutationFn: (vars: Record<string, unknown>) =>
      graphqlClient.request<{ createListing: { listing: { id: string } | null; errors: string[] } }>(
        CREATE_LISTING_MUTATION_SIBLING,
        vars
      ),
    onSuccess: (response) => {
      if (response.createListing.listing) {
        queryClient.invalidateQueries({ queryKey: ["listing", id] });
        setNewBoxDrawn(null);
        setSelectedGame(null);
        setGameSearch("");
        setNewPrice("");
        setNewCondition("good");
      }
    },
  });

  const handleAddBox = useCallback((box: BoundingBox) => {
    setNewBoxDrawn(box);
  }, []);

  const handleUpdateNewBox = useCallback((box: BoundingBox) => {
    setNewBoxDrawn(box);
  }, []);

  const handleRemoveSibling = useCallback((siblingId: string) => {
    if (confirm(t("listings.confirm_cancel_listing"))) {
      cancelSiblingMutation.mutate(siblingId);
    }
  }, [cancelSiblingMutation, t]);

  const handleRemoveCurrentBox = useCallback(() => {
    if (!listing) return;
    if (confirm(t("listings.confirm_remove_region"))) {
      clearBboxMutation.mutate(listing.id);
    }
  }, [listing, clearBboxMutation]);

  const handleUpdateActiveBox = useCallback((box: BoundingBox) => {
    if (!listing) return;
    graphqlClient.request(UPDATE_LISTING_BBOX_MUTATION, {
      id: listing.id,
      bboxYMin: box.yMin,
      bboxXMin: box.xMin,
      bboxYMax: box.yMax,
      bboxXMax: box.xMax,
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: ["listing", id] });
    });
  }, [listing, id, queryClient]);

  const handleUpdateSiblingBox = useCallback((siblingId: string, box: BoundingBox) => {
    graphqlClient.request(UPDATE_LISTING_BBOX_MUTATION, {
      id: siblingId,
      bboxYMin: box.yMin,
      bboxXMin: box.xMin,
      bboxYMax: box.yMax,
      bboxXMax: box.xMax,
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: ["listing", id] });
    });
  }, [id, queryClient]);

  const handleCreateSibling = useCallback(() => {
    if (!listing || !newBoxDrawn || !newPrice) return;
    if (!selectedGame && !gameSearch.trim()) return;
    createSiblingMutation.mutate({
      ...(selectedGame ? { gameId: selectedGame.id } : { title: gameSearch.trim() }),
      price: parseFloat(newPrice),
      condition: newCondition,
      listingGroupId: listing.listingGroupId,
      bboxYMin: newBoxDrawn.yMin,
      bboxXMin: newBoxDrawn.xMin,
      bboxYMax: newBoxDrawn.yMax,
      bboxXMax: newBoxDrawn.xMax,
      deliveryAvailable: listing.deliveryAvailable,
      pickupAvailable: listing.pickupAvailable,
    });
  }, [listing, selectedGame, gameSearch, newBoxDrawn, newPrice, newCondition, createSiblingMutation]);

  usePageTitle(
    listing ? `${listing.displayTitle} - ${listing.price?.toFixed(2)} EUR` : null
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-500">{t("listings.not_found")}</p>
        <Link
          to="/listings"
          className="mt-4 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
        >
          {t("listings.back_to_listings")}
        </Link>
      </div>
    );
  }

  const isOwner = user?.id === listing.seller.id;
  const isSold = listing.status === "sold";
  const isReserved = listing.status === "reserved";
  const isCancelled = listing.status === "cancelled";

  if (isCancelled && !isOwner) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("listings.unavailable_title")}
        </h1>
        <p className="mt-3 text-gray-600">{t("listings.unavailable_description")}</p>
        <Link
          to="/listings"
          className="mt-6 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
        >
          {t("listings.back_to_listings")}
        </Link>
      </div>
    );
  }

  const images =
    listing.imageUrls.length > 0
      ? listing.imageUrls
      : listing.game?.imageUrl
        ? [listing.game.imageUrl]
        : [];

  const hasBoundingBoxOverlay =
    listing.boundingBox &&
    listing.siblingListings &&
    listing.siblingListings.length > 0;

  const canEditBoxes = isOwner && listing.listingGroupId && listing.status === "active";

  const fullDate = new Date(listing.createdAt).toLocaleDateString("lt-LT", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const statusBadge = isSold ? (
    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
      {t("listings.status_sold")}
    </span>
  ) : isReserved ? (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
      {t("listings.status_reserved")}
    </span>
  ) : isCancelled ? (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
      {t("listings.status_cancelled")}
    </span>
  ) : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <nav className="mb-6 text-sm text-gray-500">
        <Link to="/listings" className="hover:text-primary-800">
          {t("listings.title")}
        </Link>
        <span className="mx-2">/</span>
        {listing.game ? (
          <Link
            to={`/games/${listing.game.id}`}
            className="hover:text-primary-800"
          >
            {listing.displayTitle}
          </Link>
        ) : (
          <span className="text-gray-900">{listing.displayTitle}</span>
        )}
      </nav>

      <div className="grid gap-8 lg:grid-cols-5">
        {/* Image gallery */}
        <div className="lg:col-span-3">
          {images.length > 0 ? (
            <>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                {editMode && selectedImage === 0 ? (
                  <BoundingBoxOverlay
                    mode="edit"
                    activeBoundingBox={listing.boundingBox ?? null}
                    siblings={listing.siblingListings}
                    imageUrl={images[0]}
                    newBox={newBoxDrawn}
                    onAddBox={handleAddBox}
                    onUpdateNewBox={handleUpdateNewBox}
                    onRemoveSibling={handleRemoveSibling}
                    onRemoveCurrentBox={handleRemoveCurrentBox}
                    onUpdateActiveBox={handleUpdateActiveBox}
                    onUpdateSiblingBox={handleUpdateSiblingBox}
                  />
                ) : hasBoundingBoxOverlay && selectedImage === 0 ? (
                  <ZoomedListingImage
                    imageUrl={images[0]}
                    boundingBox={listing.boundingBox!}
                    alt={listing.displayTitle}
                    className="w-full"
                  />
                ) : (
                  <img
                    src={images[selectedImage]}
                    alt={listing.displayTitle}
                    className="w-full"
                  />
                )}
              </div>
              {/* Edit mode toggle button */}
              {canEditBoxes && (
                <div className="mt-3">
                  <button
                    onClick={() => { setEditMode(!editMode); setNewBoxDrawn(null); }}
                    className={`text-sm font-medium ${editMode ? "btn-secondary" : "text-primary-800 hover:text-primary-700"}`}
                  >
                    {editMode ? t("listings.finish_editing") : t("listings.edit_regions")}
                  </button>
                </div>
              )}
              {/* New box form */}
              {editMode && newBoxDrawn && (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.new_listing_for_region")}</h4>
                  {/* Game search */}
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-gray-600">{t("listings.game")}</label>
                    {selectedGame ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{selectedGame.title}</span>
                        <button
                          onClick={() => { setSelectedGame(null); setGameSearch(""); }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          {t("listings.change")}
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="text"
                          value={gameSearch}
                          onChange={(e) => setGameSearch(e.target.value)}
                          placeholder={t("listings.search_game_placeholder")}
                          className="input w-full text-sm"
                        />
                        {searchResults?.searchGames && searchResults.searchGames.length > 0 && (
                          <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                            {searchResults.searchGames.map((game) => (
                              <button
                                key={game.id}
                                onClick={() => { setSelectedGame(game); setGameSearch(game.title); }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                              >
                                {game.thumbnailUrl && (
                                  <img src={game.thumbnailUrl} className="h-8 w-8 rounded object-cover" alt="" />
                                )}
                                <span>{game.title} {game.yearPublished ? `(${game.yearPublished})` : ""}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Price + Condition */}
                  <div className="mb-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">{t("listings.price_eur")}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        className="input w-full text-sm"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">{t("listings.condition")}</label>
                      <select
                        value={newCondition}
                        onChange={(e) => setNewCondition(e.target.value)}
                        className="input w-full text-sm"
                      >
                        {CONDITION_KEYS.map((key) => (
                          <option key={key} value={key}>{t(`conditions.${key}`)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateSibling}
                      disabled={(!selectedGame && !gameSearch.trim()) || !newPrice || createSiblingMutation.isPending}
                      className="btn-primary text-sm"
                    >
                      {createSiblingMutation.isPending ? t("listings.creating") : t("listings.create_listing")}
                    </button>
                    <button
                      onClick={() => setNewBoxDrawn(null)}
                      className="btn-secondary text-sm"
                    >
                      {t("listings.cancel")}
                    </button>
                  </div>
                </div>
              )}
              {images.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {images.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                        idx === selectedImage
                          ? "border-primary-800"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={url}
                        alt={`${listing.displayTitle} ${idx + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <ListingPlaceholder className="aspect-square rounded-xl border border-gray-200" />
          )}
        </div>

        {/* Purchase card (sticky on desktop) */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="card p-6">
              {statusBadge && <div className="mb-4">{statusBadge}</div>}

              {/* Title + game link */}
              {listing.game ? (
                <Link
                  to={`/games/${listing.game.id}`}
                  className="group/game flex items-start gap-3"
                >
                  {listing.game.thumbnailUrl && (
                    <img
                      src={listing.game.thumbnailUrl}
                      alt={listing.game.title}
                      className="h-14 w-14 shrink-0 rounded-lg border border-gray-200 object-cover shadow-sm transition-shadow group-hover/game:shadow-md"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="block text-lg font-semibold leading-tight text-primary-800 group-hover/game:text-primary-700">
                      {listing.displayTitle}
                      {listing.game.yearPublished && (
                        <span className="ml-1 text-sm font-normal text-gray-500">({listing.game.yearPublished})</span>
                      )}
                    </span>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                      {listing.game.playerRange && <span>{listing.game.playerRange} žaidėjai</span>}
                      {listing.game.playtimeRange && <span>{listing.game.playtimeRange}</span>}
                      {listing.game.complexityLabel && <span>{listing.game.complexityLabel}</span>}
                      {listing.game.displayRating != null && (
                        <RatingBadge rating={listing.game.displayRating} source={listing.game.ratingSource ?? null} />
                      )}
                    </div>
                  </div>
                </Link>
              ) : (
                <p className="text-lg font-semibold leading-tight text-gray-900">
                  {listing.displayTitle}
                </p>
              )}

              {/* Price */}
              <div className="mt-5">
                <p className="text-3xl font-bold text-gray-900">
                  {listing.price != null
                    ? `${listing.price.toFixed(2)} EUR`
                    : t("listings.price_not_set")}
                </p>
                {listing.shippingAvailable && listing.shippingCost != null && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t("listings.shipping_cost_amount", { cost: listing.shippingCost.toFixed(2) })}
                    <span className="ml-2 font-medium text-gray-700">
                      {t("listings.total_price", { price: listing.totalPrice.toFixed(2) })}
                    </span>
                  </p>
                )}
                {listing.game?.shopListings && listing.game.shopListings.length > 0 && (
                  <ListingPriceComparison
                    shopListings={listing.game.shopListings}
                    listingPrice={listing.price}
                    condition={listing.condition}
                    conditionLabel={listing.conditionLabel}
                  />
                )}
              </div>

              {/* Condition + language row */}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">{t("listings.condition")}</span>
                  <ConditionBadge condition={listing.condition} />
                </div>
                {listing.languageLabel && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">{t("listings.language")}</span>
                    <span className="text-sm font-medium text-gray-900">{listing.languageLabel}</span>
                  </div>
                )}
              </div>
              {listing.conditionNotes && (
                <p className="mt-2 text-sm text-gray-600">{listing.conditionNotes}</p>
              )}

              {/* Delivery / pickup compact */}
              <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-4 text-sm">
                {(listing.deliveryAvailable || listing.shippingAvailable) ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>
                      {t("listings.delivery_available_short")}
                      {listing.shippingCost != null && (
                        <span className="text-gray-500"> ({listing.shippingCost.toFixed(2)} EUR)</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t("listings.delivery_not_available")}
                  </div>
                )}
                {listing.pickupAvailable ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>
                      {t("listings.pickup_label")}
                      {listing.pickupCity && ` — ${listing.pickupCity}`}
                      {listing.pickupRadius != null && listing.pickupRadius > 0 && (
                        <span className="text-gray-500"> ({t("listings.pickup_radius_km", { radius: listing.pickupRadius })})</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t("listings.pickup_not_available")}
                  </div>
                )}
                {listing.location && !listing.pickupCity && (
                  <p className="text-gray-600">
                    {t("listings.location_label", { location: listing.location })}
                  </p>
                )}
              </div>

              {/* Primary CTA */}
              <div className="mt-5 flex flex-col gap-2">
                {user ? (
                  <>
                    {!isOwner && listing.status === "active" && (
                      <Link
                        to={`/messages?listing=${listing.id}&seller=${listing.seller.id}`}
                        className="btn-primary w-full"
                      >
                        {t("listings.message_seller")}
                      </Link>
                    )}
                    {isOwner && (listing.status === "active" || listing.status === "reserved") && (
                      <Link
                        to={`/listings/${listing.id}/edit`}
                        className="btn-secondary w-full"
                      >
                        {t("listings.edit_listing")}
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <Link
                      to={`/login?redirect_to=${encodeURIComponent(`/messages?listing=${listing.id}&seller=${listing.seller.id}`)}`}
                      className="btn-primary w-full"
                    >
                      {t("listings.message_seller")}
                    </Link>
                    <p className="text-center text-xs text-gray-500">
                      {t("listings.login_hint")}
                    </p>
                  </>
                )}
              </div>

              {/* Save listing CTA — separate from messaging */}
              {!isOwner && listing.status === "active" && (
                <div className="mt-2">
                  <SaveListingButton
                    listingId={listing.id}
                    initialSaved={!!listing.savedByMe}
                  />
                </div>
              )}

              {/* Smarter freshness signal */}
              <div className="mt-4" title={fullDate}>
                <ListingFreshness createdAt={listing.createdAt} />
              </div>
            </div>

            {/* Seller trust block */}
            <SellerTrustBlock seller={listing.seller} />
          </div>
        </div>
      </div>

      {/* Game synopsis (2-line preview) */}
      {listing.game?.description && (
        <section className="mt-10 rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Apie žaidimą
          </h2>
          <div
            className="line-clamp-3 text-sm leading-relaxed text-gray-700"
            dangerouslySetInnerHTML={{ __html: listing.game.description }}
          />
          {listing.game && (
            <Link
              to={`/games/${listing.game.id}`}
              className="mt-3 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
            >
              {t("listings.game_more_details")}
            </Link>
          )}
        </section>
      )}

      {/* Owner status management */}
      {isOwner && (
        <section className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            {t("listings.change_status")}
          </h3>
          <div className="flex flex-wrap gap-2">
            {listing.status !== "active" && (
              <button
                onClick={() => updateStatusMutation.mutate({ id: listing.id, status: "active" })}
                disabled={updateStatusMutation.isPending}
                className="rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 disabled:opacity-50"
              >
                {t("listings.action_activate")}
              </button>
            )}
            {listing.status !== "reserved" && listing.status !== "cancelled" && (
              <button
                onClick={() => updateStatusMutation.mutate({ id: listing.id, status: "reserved" })}
                disabled={updateStatusMutation.isPending}
                className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
              >
                {t("listings.action_reserve")}
              </button>
            )}
            {listing.status !== "sold" && listing.status !== "cancelled" && (
              <button
                onClick={() => updateStatusMutation.mutate({ id: listing.id, status: "sold" })}
                disabled={updateStatusMutation.isPending}
                className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50"
              >
                {t("listings.action_mark_sold")}
              </button>
            )}
            {listing.status !== "cancelled" && (
              <button
                onClick={() => updateStatusMutation.mutate({ id: listing.id, status: "cancelled" })}
                disabled={updateStatusMutation.isPending}
                className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
              >
                {t("listings.action_remove")}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Sibling listings from same image */}
      {listing.siblingListingDetails && listing.siblingListingDetails.length > 0 && (
        <section className="mt-12">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {t("listings.siblings_title")}
            </h2>
            {listing.listingGroupId && (
              <Link
                to={`/listing-groups/${listing.listingGroupId}`}
                className="text-sm font-medium text-primary-800 hover:text-primary-700"
              >
                {t("collections.view_collection")}
              </Link>
            )}
          </div>
          {listing.boundingBox && images[0] ? (
            <SiblingsPhotoMap
              imageUrl={images[0]}
              activeListing={{
                id: listing.id,
                displayTitle: listing.displayTitle,
                price: listing.price,
                condition: listing.condition,
                status: listing.status,
                boundingBox: listing.boundingBox,
                thumbnailUrl: listing.game?.thumbnailUrl ?? null,
              }}
              siblings={listing.siblingListingDetails}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {listing.siblingListingDetails.map((sibling) => (
                <ListingCard key={sibling.id} listing={sibling} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Other seller listings */}
      {listing.sellerOtherListings && listing.sellerOtherListings.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-6 text-2xl font-bold text-gray-900">
            {t("listings.seller_other_listings")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listing.sellerOtherListings.map((other) => (
              <ListingCard key={other.id} listing={other} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
